import React, { useState, useEffect } from 'react';
import { Menu, X, Settings, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'inicio', label: 'Inicio' },
    { id: 'nosotros', label: 'Nosotros' },
    { id: 'servicios', label: 'Servicios' },
    { id: 'jugadores', label: 'Jugadores' },
    { id: 'staff', label: 'Staff' },
    { id: 'contacto', label: 'Contacto' }
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-black/95 backdrop-blur-xl shadow-2xl py-3'
            : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer group"
            onClick={() => scrollToSection('inicio')}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_faro-player-showcase/artifacts/6o32bteg_Logo_Blanco.png"
              alt="FARO Sports"
              className="h-12 sm:h-14 lg:h-16 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="relative px-4 py-2.5 text-sm font-medium text-white/70 hover:text-white transition-colors duration-300 group"
              >
                <span className="relative z-10">{item.label}</span>
                <div className="absolute inset-0 bg-white/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-white group-hover:w-1/2 transition-all duration-300" />
              </button>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://scout.farosports.com.uy"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-all duration-300"
              title="Faro Scout"
            >
              <Lock size={18} />
            </a>
            <Link
              to="/admin"
              className="flex items-center gap-2 px-4 py-2.5 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-all duration-300"
              title="Panel de administración"
            >
              <Settings size={18} />
            </Link>
            <button
              onClick={() => scrollToSection('contacto')}
              className="flex items-center gap-2 px-6 py-2.5 bg-white text-black font-semibold rounded-full hover:bg-white/90 transition-all duration-300 hover:shadow-lg hover:shadow-white/20 hover:-translate-y-0.5"
            >
              <span>Únete al equipo</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={() => setIsMobileMenuOpen(false)} />
        <nav className={`absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-black p-6 pt-24 transform transition-transform duration-500 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex flex-col gap-1">
            {navItems.map((item, index) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-left px-4 py-4 text-lg font-medium text-white/70 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-300"
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                {item.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => scrollToSection('contacto')}
            className="w-full mt-6 px-6 py-4 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-all duration-300"
          >
            Únete al equipo
          </button>
          <a
            href="https://scout.farosports.com.uy"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full mt-3 px-6 py-4 border border-white/20 text-white/70 font-medium rounded-xl hover:bg-white/5 hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <Lock size={18} />
            <span>Faro Scout</span>
          </a>
          <Link
            to="/admin"
            className="w-full mt-3 px-6 py-4 border border-white/20 text-white/70 font-medium rounded-xl hover:bg-white/5 hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <Settings size={18} />
            <span>Panel Admin</span>
          </Link>
        </nav>
      </div>
    </>
  );
};

export default Header;
